'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { ArrowRight, Bell, CheckCheck } from 'lucide-react'
import { cn, formatPrice, proxyImageUrl } from '@/lib/utils'
import { api } from '@/lib/api'
import { useAuth } from '@/components/auth'
import { PhotoBlock, isLikelyMonthlyPrice, paletteFor } from '@/components/roostr'
import { relTime } from '@/components/notifications/notif-row'
import { WHATS_NEW_SEEN_ALL_EVENT } from '@/components/notifications/use-review-queue'
import { navPillCls, navPillBadgeCls, navPillLabelCls } from './nav-pill'
import { useI18n } from '@/lib/i18n'
import type { NotificationPreviewListing } from '@/lib/types'

const POLL_INTERVAL_MS = 60_000
// Badge display caps at 99+, so counting stops once the cap is proven.
const BADGE_CAP = 99
const COUNT_PAGE_LIMIT = 100 // backend max
const MAX_COUNT_PAGES = 2
// Route changes refetch the badge, but never more often than this — rapid
// navigation must not stack requests on a busy backend.
const REFETCH_TTL_MS = 15_000
// How many recent homes the dropdown holds. The list scrolls inside its own
// region (see TEASER_VIEWPORT) so the panel still shows ~5 at a time — the
// extra rows cost no extra requests, since the single getNotifications call
// already returns 30 notifications with their preview listings inline.
const TEASER_COUNT = 15
// Height of the scrollable row region ≈ 5 rows (each is a 40px thumb + py-2
// + 1px divider = 57px). Header and "View all" sit outside it and stay put.
const TEASER_VIEWPORT = 285

interface TeaserItem {
  listingId: string
  notificationId: string
  source: string
  at: string
  listing?: NotificationPreviewListing
  /** Read state as of this fetch's server response (see fetchTeaser: the
   * backend returns the newest notifications regardless of read status —
   * this is NOT a client-side unread filter). */
  read: boolean
}

// Header bell — design shell.jsx BellMenu (2026-07-15 revision): "dropdown =
// teaser & router, not a triage surface". The newest few homes, then "Mark
// all seen" / "View all" into the What's-new inbox. The trigger is the
// design's labeled "What's new" nav pill (2026-07-16 nav-group rework;
// supersedes the earlier icon-only adaptation) — label + inline badge,
// icons-only below xl. Badge counts what the inbox counts: unique listing ids
// across UNREAD notifications (NOT counts-by-search, which drops
// orphaned-search notifications the inbox still shows).
export function NotificationBell() {
  const router = useRouter()
  const pathname = usePathname()
  const { user } = useAuth()
  const { t, uiLanguage } = useI18n()
  const [unreadCount, setUnreadCount] = React.useState(0)
  const [open, setOpen] = React.useState(false)
  const [teaser, setTeaser] = React.useState<TeaserItem[] | null>(null)
  // Notification ids clicked this session — rows stay visible in the teaser,
  // just restyled from "new" (red) to regular, matching the pre-redesign
  // behavior. Separate from pendingReadIdsRef below, which guards the badge
  // COUNT specifically; this one only ever affects row appearance.
  const [readNotifIds, setReadNotifIds] = React.useState<Set<string>>(new Set())
  const rootRef = React.useRef<HTMLSpanElement>(null)
  const inFlightRef = React.useRef(false)
  const lastFetchRef = React.useRef(0)
  const teaserSeqRef = React.useRef(0)
  // Notification ids marked read locally (openListing) whose write may still
  // be in flight server-side. openListing navigates right after firing
  // markNotificationRead (without awaiting it) so the dropdown feels instant
  // — but that navigation changes `pathname`, which triggers a background
  // fetchCount refresh. If that GET lands before the read write is
  // committed, the server still reports the notification unread and would
  // silently undo the optimistic badge decrement. Filtering fetchCount
  // through this set makes the drop stick regardless of ordering. Read-state
  // is one-way server-side, so an id only ever needs to come back out on a
  // confirmed markNotificationRead failure.
  const pendingReadIdsRef = React.useRef<Set<string>>(new Set())

  const fetchCount = React.useCallback(async (force = false) => {
    if (!user) {
      setUnreadCount(0)
      return
    }
    if (inFlightRef.current) return
    if (!force && Date.now() - lastFetchRef.current < REFETCH_TTL_MS) return
    inFlightRef.current = true
    lastFetchRef.current = Date.now()
    try {
      // Lightweight pages (no hydration) until the cap is exceeded or the
      // list ends. May undercount past MAX_COUNT_PAGES notifications, but by
      // then the badge reads 99+ anyway.
      const ids = new Set<string>()
      let offset = 0
      for (let page = 0; page < MAX_COUNT_PAGES; page++) {
        const res = await api.getNotifications(COUNT_PAGE_LIMIT, offset, false)
        res.notifications.forEach((n) => {
          if (!n.read && !pendingReadIdsRef.current.has(n.id)) (n.listing_ids ?? []).forEach((id) => ids.add(id))
        })
        offset += res.notifications.length
        if (ids.size > BADGE_CAP || res.notifications.length === 0 || offset >= res.total) break
      }
      setUnreadCount(ids.size)
    } catch {
      // Keep the previous value on transient failures.
    } finally {
      inFlightRef.current = false
    }
  }, [user])

  // Fresh on sign-in and on every route change — reviewing in the inbox marks
  // notifications read server-side, so leaving the page re-syncs the badge.
  React.useEffect(() => {
    void fetchCount()
  }, [fetchCount, pathname])

  // Outside-mousedown closes the dropdown (design shell.jsx BellMenu).
  React.useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  // Teaser rows: newest unread listings. Uses the batched lightweight
  // preview path (include_preview=true, hydrate_listings=false) so the whole
  // dropdown resolves in ONE request — previously this fired an extra GET
  // /listings/{id} per row (up to TEASER_COUNT), which is what made the
  // dropdown feel slow to open (see "Optimize notification dropdown latency"
  // — the preview path existed but this call site had stopped using it).
  const fetchTeaser = React.useCallback(async () => {
    if (!user) return
    const seq = ++teaserSeqRef.current
    try {
      const res = await api.getNotifications(30, 0, false, true)
      if (seq !== teaserSeqRef.current) return
      const previewById = new Map<string, NotificationPreviewListing>()
      res.notifications.forEach((n) => {
        n.preview_listings?.forEach((p) => previewById.set(p.id, p))
      })
      const seen = new Set<string>()
      const items: TeaserItem[] = []
      // The endpoint returns the newest notifications regardless of read
      // status (no server-side unread filter) — so a row a user just marked
      // read keeps showing here, just with `read: true`, instead of being
      // thrown away and needing to be re-derived from readNotifIds forever.
      // It only drops off the teaser once genuinely new matches push it past
      // TEASER_COUNT, same as the old per-notification dropdown.
      ;[...res.notifications]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .forEach((n) => {
          ;(n.listing_ids ?? []).forEach((id) => {
            if (items.length >= TEASER_COUNT || seen.has(id)) return
            seen.add(id)
            items.push({
              listingId: id,
              notificationId: n.id,
              source: n.search_name || 'Saved search',
              at: n.created_at,
              listing: previewById.get(id),
              read: n.read,
            })
          })
        })
      setTeaser(items)
      // Drop stale entries (fell out of the teaser window entirely) so this
      // can't grow forever across a long session.
      setReadNotifIds((prev) => {
        const stillPresent = new Set(items.map((i) => i.notificationId))
        const next = new Set([...prev].filter((id) => stillPresent.has(id)))
        return next.size === prev.size ? prev : next
      })
    } catch {
      if (seq === teaserSeqRef.current) setTeaser([])
    }
  }, [user])

  // Warm the teaser the same way the badge count is warmed above: fetch it
  // in the background as soon as we land on a page (and again on poll/
  // focus), so the FIRST click of the bell doesn't pay for a visible round
  // trip either. Header/NotificationBell is mounted once in the root layout
  // and survives client-side navigation, but this still matters for the
  // very first click of a session (or after a hard reload, which remounts
  // it and drops the cached teaser). toggleOpen still refetches on click to
  // stay current, but by then `teaser` is usually already populated.
  React.useEffect(() => {
    void fetchTeaser()
  }, [fetchTeaser, pathname])

  React.useEffect(() => {
    if (!user) return
    const id = setInterval(() => {
      void fetchCount(true)
      void fetchTeaser()
    }, POLL_INTERVAL_MS)
    const onFocus = () => {
      void fetchCount()
      void fetchTeaser()
    }
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [user, fetchCount, fetchTeaser])

  const toggleOpen = () => {
    setOpen((v) => {
      // Only the very first open (teaser === null) shows the loading
      // placeholder. Once we have a cached teaser, leave it on screen and
      // refresh it silently in the background (fetchTeaser replaces it once
      // resolved) — reopening the dropdown never re-blocks on a round trip.
      // This is the "non-blocking refresh" half of the original
      // dropdown-latency optimization; without it, every open re-nulled the
      // teaser and forced a wait even when fresh data was already cached.
      if (!v) void fetchTeaser()
      return !v
    })
  }

  const goToInbox = () => {
    setOpen(false)
    router.push('/notifications')
  }

  // Restores pre-redesign behavior: opening a flat from the dropdown marks
  // it read and drains the badge, instead of leaving the count stuck until
  // the user visits /notifications. Read-state is per NOTIFICATION
  // server-side (no per-listing endpoint), so clicking one row marks its
  // whole notification read — any sibling rows from the same notification
  // still visible in the teaser drop the "new" styling too, since the server
  // now considers them read as well. The row itself STAYS in the list (like
  // the old page did) instead of vanishing — it just switches from the red
  // "new" look to the regular one, and only drops off once genuinely newer
  // matches push it past TEASER_COUNT.
  const openListing = (it: TeaserItem) => {
    setOpen(false)
    pendingReadIdsRef.current.add(it.notificationId)
    // Only still-unread siblings count against the badge — teaser can now
    // include already-read history rows too (see fetchTeaser).
    const siblings = teaser?.filter((p) => p.notificationId === it.notificationId && !p.read) ?? []
    if (siblings.length > 0) setUnreadCount((c) => Math.max(0, c - siblings.length))
    setReadNotifIds((prev) => new Set(prev).add(it.notificationId))
    api.markNotificationRead(it.notificationId).catch(() => {
      // The write never landed — stop overriding the server's (still
      // unread) answer and let the next fetch put the badge/styling back.
      pendingReadIdsRef.current.delete(it.notificationId)
      setReadNotifIds((prev) => {
        const next = new Set(prev)
        next.delete(it.notificationId)
        return next
      })
      void fetchCount(true)
      void fetchTeaser()
    })
    router.push(`/listings/${it.listingId}`)
  }

  // "Clear the counter — homes stay browsable in View all": blanket read-all.
  // Rows stay put (same as clicking one individually) and just lose the red
  // "new" styling — only the badge count actually clears; a mounted inbox
  // page re-syncs through the window event.
  const markAllSeen = () => {
    setUnreadCount(0)
    setReadNotifIds((prev) => {
      const next = new Set(prev)
      teaser?.forEach((it) => next.add(it.notificationId))
      return next
    })
    api
      .markAllNotificationsRead()
      .then(() => window.dispatchEvent(new Event(WHATS_NEW_SEEN_ALL_EVENT)))
      .catch(() => {
        void fetchCount(true)
        void fetchTeaser()
      })
  }

  if (!user) return null

  const active = pathname.startsWith('/notifications') || open

  return (
    <span ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={t('notifications.whatsNew', "What's new")}
        title={t('notifications.bellTip', "What's new — fresh matches")}
        className={cn(navPillCls(active), 'outline-none')}
      >
        <Bell className={cn('h-[18px] w-[18px]', active && 'fill-current')} />
        <span className={navPillLabelCls}>{t('notifications.whatsNew', "What's new")}</span>
        {unreadCount > 0 && (
          <span className={navPillBadgeCls}>{unreadCount > BADGE_CAP ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'z-[80] animate-pop-in rounded-shape-md border border-outline bg-surface shadow-e3',
            // <sm: pin to the VIEWPORT, not the bell. `right-0` anchors the
            // panel's right edge to the bell, which sits ~275px from the left
            // on a phone — so a 351px-wide panel started at x=-76 and its
            // title and "Show all" were cut off the left edge. max-w alone
            // can't fix that: it caps the width, not the position.
            'fixed inset-x-3 top-[100px] w-auto max-w-none',
            // Fixed can't scroll into view the way the absolute panel could,
            // so cap it to the space below the header and scroll inside.
            'max-h-[calc(100dvh-116px)] overflow-y-auto overflow-x-hidden',
            // ≥sm: the original bell-anchored dropdown, unchanged.
            'sm:absolute sm:inset-x-auto sm:right-0 sm:top-[calc(100%+10px)] sm:max-h-none sm:w-[384px] sm:max-w-[calc(100vw-24px)] sm:overflow-hidden'
          )}
        >
          <div className="flex items-center gap-2 px-4 pb-[10px] pt-3">
            <span className="text-[14px] font-bold text-ink">{t('notifications.newHomes', 'New homes')}</span>
            <span className="font-mono text-[11.5px] font-bold text-ink-muted">
              {unreadCount > BADGE_CAP ? '99+' : unreadCount}
            </span>
            <span className="flex-1" />
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllSeen}
                title={t('notifications.bellMarkAllTip', 'Clear the counter — homes stay browsable in View all')}
                className="flex items-center gap-1 text-[12px] font-semibold text-ink-secondary transition-colors hover:text-ink"
              >
                <CheckCheck size={15} /> {t('notifications.markAllSeenShort', 'Mark all seen')}
              </button>
            )}
          </div>
          {/* Rows scroll in their own region so the header and the "View all"
              footer stay pinned; the panel itself no longer grows with the
              list. overscroll-contain stops a flick at the end of the rows
              from scrolling the page behind the dropdown. */}
          <div
            className="overflow-y-auto overscroll-contain"
            style={{ maxHeight: TEASER_VIEWPORT }}
          >
          {teaser === null ? (
            <div className="border-t border-outline-variant px-4 py-[18px] text-[12.5px] text-ink-muted">
              {t('notifications.loading', 'Loading...')}
            </div>
          ) : teaser.length > 0 ? (
            teaser.map((it) => {
              // "New" = unread per the last fetch AND not locally marked
              // read this session (readNotifIds covers the click-to-read
              // race: the server may not have committed the write yet by
              // the time a background refetch lands). The row itself always
              // stays in the list either way — it only drops off once
              // genuinely newer matches push it past TEASER_COUNT.
              const isNew = !it.read && !readNotifIds.has(it.notificationId)
              return (
                <button
                  key={it.listingId}
                  type="button"
                  onClick={() => openListing(it)}
                  className={cn(
                    'flex w-full items-center gap-[11px] border-t border-outline-variant px-4 py-2 text-left transition-colors',
                    isNew ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-surface-low'
                  )}
                >
                  <span className="relative block h-10 w-[52px] shrink-0 overflow-hidden rounded-lg">
                    <PhotoBlock palette={paletteFor(it.listingId)} className="absolute inset-0 h-full w-full" />
                    {it.listing?.image_url ? (
                      <img
                        src={proxyImageUrl(it.listing.image_url)}
                        alt={it.listing.title || ''}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : null}
                    {isNew ? (
                      <span className="absolute left-[3px] top-[3px] rounded bg-primary px-[5px] py-[1px] font-mono text-[8.5px] font-bold uppercase text-primary-foreground">
                        {t('searches.newBadge', 'New')}
                      </span>
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    {it.listing ? (
                      <>
                        <span className="block truncate text-[13px] font-semibold text-ink">{it.listing.title}</span>
                        <span className="mt-px block truncate text-[11.5px] text-ink-muted">
                          {it.listing.price != null ? (
                            <span className="font-mono font-bold text-ink-secondary">
                              {formatPrice(it.listing.price, it.listing.price_currency)}
                              {isLikelyMonthlyPrice(it.listing.price) ? t('listingCard.perMo', '/mo') : ''}
                            </span>
                          ) : null}
                          {it.listing.district ? ` · ${it.listing.district}` : ''} · {it.source}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="block h-[13px] w-3/5 animate-pulse rounded bg-surface-high" />
                        <span className="mt-1 block h-[10px] w-2/5 animate-pulse rounded bg-surface-high" />
                      </>
                    )}
                  </span>
                  <span className="shrink-0 font-mono text-[10.5px] text-ink-muted">{relTime(it.at, t, uiLanguage)}</span>
                </button>
              )
            })
          ) : (
            <div className="border-t border-outline-variant px-4 py-[18px] text-[12.5px] text-ink-muted">
              {t('notifications.bellCaughtUp', 'All caught up — new matches land here.')}
            </div>
          )}
          </div>
          <button
            type="button"
            onClick={goToInbox}
            className="flex w-full items-center justify-center gap-[6px] border-t border-outline-variant bg-surface-low px-4 py-[11px] text-[13px] font-bold text-primary transition-colors hover:bg-surface-high"
          >
            {t('notifications.viewAll', 'View all')}
            {unreadCount > 0 ? ` ${unreadCount > BADGE_CAP ? '99+' : unreadCount}` : ''} <ArrowRight size={15} />
          </button>
        </div>
      )}
    </span>
  )
}
